import {
  assertStudioCapability,
} from "./StudioCapability";
import {
  StudioCapabilityRegistryError,
} from "./errors";

function assertNonEmptyString(value, fieldName) {
  if (typeof value !== "string" || value.trim() === "") {
    throw new StudioCapabilityRegistryError(
      `${fieldName} must be a non-empty string.`,
      "INVALID_REGISTRY_IDENTIFIER"
    );
  }

  return value.trim();
}

function compareCapabilities(left, right) {
  if (left.capability.priority !== right.capability.priority) {
    return right.capability.priority - left.capability.priority;
  }

  return left.capability.id.localeCompare(right.capability.id);
}

export function createStudioCapabilityRegistry() {
  const capabilities = new Map();

  function register(pluginId, capability) {
    const normalizedPluginId = assertNonEmptyString(
      pluginId,
      "pluginId"
    );

    assertStudioCapability(capability);

    if (capabilities.has(capability.id)) {
      throw new StudioCapabilityRegistryError(
        `Studio capability "${capability.id}" is already registered.`,
        "DUPLICATE_CAPABILITY_ID"
      );
    }

    capabilities.set(capability.id, Object.freeze({
      pluginId: normalizedPluginId,
      capability,
    }));

    return capability;
  }

  function unregister(pluginId, capabilityId) {
    const normalizedPluginId = assertNonEmptyString(
      pluginId,
      "pluginId"
    );
    const normalizedCapabilityId = assertNonEmptyString(
      capabilityId,
      "capabilityId"
    );
    const entry = capabilities.get(normalizedCapabilityId);

    if (!entry) {
      return false;
    }

    if (entry.pluginId !== normalizedPluginId) {
      throw new StudioCapabilityRegistryError(
        `Studio capability "${normalizedCapabilityId}" does not belong to plugin "${normalizedPluginId}".`,
        "CAPABILITY_PLUGIN_MISMATCH"
      );
    }

    return capabilities.delete(normalizedCapabilityId);
  }

  function unregisterPlugin(pluginId) {
    const normalizedPluginId = assertNonEmptyString(
      pluginId,
      "pluginId"
    );

    let removedCount = 0;

    for (const [capabilityId, entry] of capabilities.entries()) {
      if (entry.pluginId === normalizedPluginId) {
        capabilities.delete(capabilityId);
        removedCount += 1;
      }
    }

    return removedCount;
  }

  function get(capabilityId) {
    const normalizedCapabilityId = assertNonEmptyString(
      capabilityId,
      "capabilityId"
    );

    return capabilities.get(normalizedCapabilityId)?.capability || null;
  }

  function has(capabilityId) {
    const normalizedCapabilityId = assertNonEmptyString(
      capabilityId,
      "capabilityId"
    );

    return capabilities.has(normalizedCapabilityId);
  }

  function listEntries() {
    return [...capabilities.values()].sort(compareCapabilities);
  }

  function list() {
    return Object.freeze(
      listEntries().map((entry) => entry.capability)
    );
  }

  function listByType(type) {
    const normalizedType = assertNonEmptyString(type, "type");

    return Object.freeze(
      listEntries()
        .filter((entry) => entry.capability.type === normalizedType)
        .map((entry) => entry.capability)
    );
  }

  function listByPlugin(pluginId) {
    const normalizedPluginId = assertNonEmptyString(
      pluginId,
      "pluginId"
    );

    return Object.freeze(
      listEntries()
        .filter((entry) => entry.pluginId === normalizedPluginId)
        .map((entry) => entry.capability)
    );
  }

  function clear() {
    const removedCount = capabilities.size;
    capabilities.clear();

    return removedCount;
  }

  return Object.freeze({
    register,
    unregister,
    unregisterPlugin,
    get,
    has,
    list,
    listByType,
    listByPlugin,
    clear,
  });
}
